import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getProfile } from "@/api/profile";
import { useAuthStore } from "@/stores/auth";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";

/**
 * Keeps i18n.language in sync with the authenticated user's saved language.
 *
 * Runs once the user is logged in (token present). Pulls the profile,
 * reads user.language, and calls i18n.changeLanguage() if it differs from
 * whatever the detector picked up (localStorage or browser). Also writes
 * localStorage so future sessions on this device default to the right
 * language even before the profile fetch completes.
 */
export function LanguageSync() {
  const token = useAuthStore((s) => s.token);
  const { i18n } = useTranslation();
  const profileQ = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    enabled: !!token,
    staleTime: 60_000,
  });

  useEffect(() => {
    const serverLang = profileQ.data?.language as AppLanguage | undefined;
    if (!serverLang) return;
    if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(serverLang)) return;
    if (serverLang === i18n.language) return;
    i18n.changeLanguage(serverLang);
    localStorage.setItem("mavinty_language", serverLang);
  }, [profileQ.data?.language, i18n]);

  return null;
}
